import { useMemo } from "react";
import { useOrgTimeZone } from "@/shared/utils/date/org-time";
import { timeToX } from "../utils/timelineCalculations";

/**
 * Where one day ends and the next begins, for a timeline whose axis only ever
 * says `HH:mm:ss`.
 *
 * Over a multi-day range those labels repeat every 24 hours with nothing to
 * separate them, so a segment at 03:14 could be any night in the window. These
 * are the marks that tell the operator which one.
 */
export interface DayBoundary {
  /** Instant of the boundary. */
  ms: number;
  /** Position on the scaled axis. */
  x: number;
  /** Short date of the day that STARTS here, e.g. "24 Aug". */
  label: string;
  key: number;
}

const MS_DAY = 86_400_000;
/**
 * Real zone offsets move in whole minutes, so bisecting to the minute lands
 * exactly on midnight — no need to chase milliseconds.
 */
const PRECISION_MS = 60_000;
/** A wide range with a tiny span would otherwise spin; nobody reads 400 marks. */
const MAX_BOUNDARIES = 400;

/**
 * Deliberately NOT `formatOrgLocaleTime`. That helper appends hour, minute and
 * second whenever the caller names no time field (`namesATimeField` in
 * org-time.ts), which is right for a clock reading and fatal here: a "day key"
 * carrying seconds changes every second, and the bisection below then converges
 * on the next tick instead of on midnight. Date-only output has to bypass it.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();
const formatter = (tz: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const key = `${tz}|${Object.entries(options)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([k, v]) => `${k}=${v}`)
    .join(";")}`;
  const hit = formatterCache.get(key);
  if (hit) return hit;
  const made = new Intl.DateTimeFormat(undefined, { ...options, timeZone: tz });
  formatterCache.set(key, made);
  return made;
};

const dayKey = (ms: number, tz: string): string =>
  formatter(tz, { year: "numeric", month: "2-digit", day: "2-digit" }).format(ms);

const dayLabel = (ms: number, tz: string): string =>
  formatter(tz, { day: "2-digit", month: "short" }).format(ms);

/**
 * Midnights of `tz` strictly inside `(min, max)`.
 *
 * Found by bisecting on the formatted DATE rather than by arithmetic on
 * offsets: a day is "when the date string changes", which stays true across DST
 * shifts, half-hour zones and the 23- and 25-hour days that break naive
 * `startOfDay + n * 86400000`.
 *
 * Empty when the window sits inside a single day — that is the caller's signal
 * that there is nothing to draw, and it is why a same-day timeline stays clean.
 */
export const dayBoundariesBetween = (min: number, max: number, tz: string): number[] => {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [];
  if (dayKey(min, tz) === dayKey(max, tz)) return [];

  const out: number[] = [];
  let cursor = min;
  while (out.length < MAX_BOUNDARIES) {
    const startKey = dayKey(cursor, tz);
    // A 26-hour reach clears the longest real DST day.
    let lo = cursor;
    let hi = Math.min(cursor + MS_DAY + 2 * 3_600_000, max);
    if (dayKey(hi, tz) === startKey) break;

    while (hi - lo > PRECISION_MS) {
      const mid = lo + Math.floor((hi - lo) / 2);
      if (dayKey(mid, tz) === startKey) lo = mid;
      else hi = mid;
    }
    // Bisection leaves `hi` within a minute ABOVE the boundary. Midnight always
    // falls on a whole minute (zone offsets move in whole minutes, DST included),
    // and `lo` is still the old day, so the last whole minute at or below `hi` IS
    // the boundary — snapping is exact, not an approximation.
    const boundary = Math.floor(hi / PRECISION_MS) * PRECISION_MS;
    if (boundary >= max) break;
    out.push(boundary);
    cursor = boundary;
  }
  return out;
};

export const useDayBoundaries = ({
  min,
  max,
  scaledWidth,
  enabled = true,
}: {
  min: number;
  max: number;
  scaledWidth: number;
  /** Off in live mode, whose window is minutes wide and never crosses a day. */
  enabled?: boolean;
}): DayBoundary[] => {
  const tz = useOrgTimeZone();
  return useMemo(() => {
    if (!enabled) return [];
    return dayBoundariesBetween(min, max, tz).map((ms) => ({
      ms,
      x: timeToX(ms, min, max, scaledWidth),
      label: dayLabel(ms, tz),
      key: ms,
    }));
  }, [min, max, scaledWidth, tz, enabled]);
};
